import { t } from 'ttag'
import YumButton from '../../../../components/YumButton/YumButton'
import React, { useEffect, useState, useMemo } from 'react'
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from '@material-ui/core'
import { Button } from '../../../../components/Button/Button'
import YumFormSwitch from '../../../../components/YumFormSwitch/YumFormSwitch'
import { Controller, useForm } from 'react-hook-form'

import { ReactComponent as GroupActiveIcon } from '../../../../assets/v3/icons/group-active.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as RequireTeamSelectionActiveIcon } from '../../../../assets/v3/icons/require-team-selection-active.svg'
import { ReactComponent as RequireTeamSelectionInactiveIcon } from '../../../../assets/v3/icons/require-team-selection-inactive.svg'
import { ReactComponent as DisableTeamSwitchingActiveIcon } from '../../../../assets/v3/icons/disable-team-switching-active.svg'
import { ReactComponent as DisableTeamSwitchingInactiveIcon } from '../../../../assets/v3/icons/disable-team-switching-inactive.svg'
import { ReactComponent as LockIcon } from '../../../../assets/v2/icons/lock.svg'
import { ReactComponent as LockFilledIcon } from '../../../../assets/v2/icons/lock-filled.svg'
import { ReactComponent as OrganizerActiveIcon } from '../../../../assets/v3/icons/organizer-active.svg'
import { ReactComponent as OrganizerInactiveIcon } from '../../../../assets/v3/icons/organizer-inactive.svg'
import { ReactComponent as CloseIcon } from '../../../../assets/v3/icons/close.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/trash.svg'

import YumField from '../../../../components/YumInput/YumInput'
import {
  CompanyId,
  UserCompanyDto,
  DivisionCategoryId,
  DivisionCategoryRow,
  DivisionCategoryType,
} from '../../../../services/api.types'
import NotificationsWrapper from '../../../../components/Notification/NotificationsWrapper'
import YumDialog from '../../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import DivisionCategoryTypeSelect from '../../../../components/DivisionCategoryTypeSelect'

export interface IDivisionCategorySettings {
  companyId: CompanyId
  lastUpdatedTeamSettings: number | null
  lastTeamSettingsFail: number | null
  lastUpdatedDivisionCategorySettings: number | null
  lastDivisionCategorySettingsFail: number | null
  managedCompanies: UserCompanyDto[]
  selectedGroupId: DivisionCategoryId | null
  divisionCategories: DivisionCategoryRow[]
  groupsLoading: boolean
  companyTeams: any[] // Teams data to determine if there are no teams
}

export interface IDivisionCategorySettingsDispatchProps {
  updateTeamSettings(payload: {
    companyId: string
    teamSelectionLocked: boolean
    teamSwitchingDisabled: boolean
    userCreatedTeams: boolean
    divisionRequired: boolean
    minTeamSize: number | ''
    maxTeamSize: number | ''
  }): void
  updateDivisionCategorySettings(payload: {
    categoryId: DivisionCategoryId
    customName?: string
    required?: boolean
    minSize?: number
    maxSize?: number
    selectionLocked?: boolean
    switchingDisabled?: boolean
    usersCanCreate?: boolean
  }): void
  deleteDivisionCategory(payload: { categoryId: DivisionCategoryId; companyId: CompanyId }): void
  renameDivisionCategory(payload: { groupId: DivisionCategoryId; companyId: CompanyId; customName: string }): void
  getWhoAmI(): void
  clearNotification(): void
  getDivisionCategories(payload?: { companyId: CompanyId }): void
}

type DivisionCategorySettingsFormData = {
  divisionRequired: boolean
  teamSelectionLocked: boolean
  teamSwitchingDisabled: boolean
  userCreatedTeams: boolean
  minTeamSize: number | ''
  maxTeamSize: number | ''
  customName?: string
}

export default function DivisionCategorySettings(
  props: IDivisionCategorySettings & IDivisionCategorySettingsDispatchProps,
) {
  const {
    companyId,
    lastUpdatedTeamSettings,
    lastTeamSettingsFail,
    lastUpdatedDivisionCategorySettings,
    lastDivisionCategorySettingsFail,
    managedCompanies,
    selectedGroupId,
    divisionCategories,
    groupsLoading,
    companyTeams,
    getWhoAmI,
    deleteDivisionCategory,
    renameDivisionCategory,
    updateDivisionCategorySettings,
    getDivisionCategories,
  } = props

  const [userCompany, setUserCompany] = useState(
    managedCompanies &&
      ((companyId
        ? managedCompanies.find((company) => company.companyId === companyId)
        : managedCompanies[0]) as UserCompanyDto),
  )

  // Filter division categories for the current company
  const currentCompanyCategories = useMemo(() => {
    if (!divisionCategories || !companyId) return []
    return divisionCategories.filter((category) => category.companyId === companyId)
  }, [divisionCategories, companyId])

  // Get the selected group information
  const selectedGroup = selectedGroupId ? currentCompanyCategories?.find((group) => group.id === selectedGroupId) : null

  // Determine default values based on whether we're editing teams (no selectedGroupId) or a specific division category
  const getDefaultValues = (): DivisionCategorySettingsFormData => {
    // If no selectedGroupId, we're editing teams - use userCompany values
    if (!selectedGroupId) {
      return {
        divisionRequired: userCompany?.divisionRequired || false,
        teamSelectionLocked: userCompany?.teamSelectionLocked || false,
        teamSwitchingDisabled: userCompany?.teamSwitchingDisabled || false,
        userCreatedTeams: userCompany?.userCreatedTeams || false,
        minTeamSize: userCompany?.minTeamSize ?? '',
        maxTeamSize: userCompany?.maxTeamSize ?? '',
      }
    }
    // If selectedGroupId exists, we're editing a specific division category - use divisionCategory values
    else if (selectedGroup) {
      return {
        divisionRequired: selectedGroup.required || false,
        teamSelectionLocked: selectedGroup.selectionLocked || false,
        teamSwitchingDisabled: selectedGroup.switchingDisabled || false,
        userCreatedTeams: selectedGroup.usersCanCreate || false,
        minTeamSize: selectedGroup.minSize ?? '',
        maxTeamSize: selectedGroup.maxSize ?? '',
      }
    }
    // Fallback to userCompany values
    return {
      divisionRequired: userCompany?.divisionRequired || false,
      teamSelectionLocked: userCompany?.teamSelectionLocked || false,
      teamSwitchingDisabled: userCompany?.teamSwitchingDisabled || false,
      userCreatedTeams: userCompany?.userCreatedTeams || false,
      minTeamSize: userCompany?.minTeamSize ?? '',
      maxTeamSize: userCompany?.maxTeamSize ?? '',
    }
  }

  const {
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    ...form
  } = useForm<DivisionCategorySettingsFormData>({
    shouldUnregister: false,
    defaultValues: getDefaultValues(),
  })
  const divisionRequired = watch('divisionRequired')
  const teamSelectionLocked = watch('teamSelectionLocked')
  const userCreatedTeams = watch('userCreatedTeams')
  const minTeamSize = watch('minTeamSize')
  const maxTeamSize = watch('maxTeamSize')
  const teamSwitchingDisabled = watch('teamSwitchingDisabled')

  const [modalOpen, setModalOpen] = useState(false)
  const [submitInProgress, setSubmitInProgress] = useState(false)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [categoryType, setCategoryType] = useState<DivisionCategoryType>(DivisionCategoryType.AREA)
  const [customName, setCustomName] = useState('')

  // Get used category types (excluding CUSTOM types and current category)
  const usedCategoryTypes = useMemo(() => {
    if (!currentCompanyCategories) return new Set<DivisionCategoryType>()

    const usedTypes = new Set<DivisionCategoryType>()
    currentCompanyCategories.forEach((category) => {
      // Only track non-CUSTOM types as they can be reused
      // Exclude the current category being edited
      if (category.type !== DivisionCategoryType.CUSTOM && category.id !== selectedGroupId) {
        usedTypes.add(category.type)
      }
    })
    return usedTypes
  }, [currentCompanyCategories, selectedGroupId])

  const closeModal = () => {
    setSubmitInProgress(false)
    setModalOpen(false)
    setDeleteConfirmationOpen(false)
    // To prevent visual flicker
    setTimeout(() => {
      reset()
    }, 200)
  }

  useEffect(() => {
    closeModal()
    getWhoAmI()
  }, [lastUpdatedTeamSettings])

  useEffect(() => {
    if (lastUpdatedDivisionCategorySettings) {
      closeModal()
      getWhoAmI()
      getDivisionCategories({ companyId })
    }
  }, [lastUpdatedDivisionCategorySettings])

  useEffect(() => {
    setSubmitInProgress(false)
  }, [lastTeamSettingsFail])

  useEffect(() => {
    if (lastDivisionCategorySettingsFail) {
      setSubmitInProgress(false)
    }
  }, [lastDivisionCategorySettingsFail])

  useEffect(() => {
    setUserCompany(managedCompanies.find((company) => company.companyId === companyId) as UserCompanyDto)
  }, [managedCompanies, companyId])

  // Initialize category type and custom name when selected group changes
  useEffect(() => {
    if (selectedGroup) {
      setCategoryType(selectedGroup.type)
      setCustomName(selectedGroup.type === DivisionCategoryType.CUSTOM ? selectedGroup.customName || '' : '')
    }
  }, [selectedGroup])

  useEffect(() => {
    reset(getDefaultValues())
  }, [
    userCompany.companyId,
    userCompany.divisionRequired,
    userCompany.teamSelectionLocked,
    userCompany.teamSwitchingDisabled,
    userCompany.userCreatedTeams,
    userCompany.minTeamSize,
    userCompany.maxTeamSize,
    selectedGroupId,
    selectedGroup?.required,
    selectedGroup?.selectionLocked,
    selectedGroup?.switchingDisabled,
    selectedGroup?.usersCanCreate,
    selectedGroup?.minSize,
    selectedGroup?.maxSize,
  ])

  const handleCancel = () => {
    closeModal()
  }

  const onSubmit = handleSubmit((newData) => {
    const { updateTeamSettings, updateDivisionCategorySettings, renameDivisionCategory } = props
    setSubmitInProgress(true)

    // Validate custom name is required when category type is CUSTOM
    if (categoryType === DivisionCategoryType.CUSTOM && !customName.trim()) {
      setError('customName', {
        type: 'required',
        message: t`Custom name is required`,
      })
      setSubmitInProgress(false)
      return
    }

    // Clear any existing custom name errors
    clearErrors('customName')

    // Check if there are no teams - use new endpoint
    const hasNoTeams = !companyTeams || companyTeams.length === 0

    // ALWAYS use new endpoint for now
    const forceUseNewEndpoint = true

    if (hasNoTeams || forceUseNewEndpoint) {
      // Use the new UpdateDivisionCategorySettings endpoint when no teams exist
      if (selectedGroupId) {
        const payload = {
          categoryId: selectedGroupId,
          type: categoryType, // Send the selected category type
          customName: categoryType === DivisionCategoryType.CUSTOM ? customName.trim() : undefined,
          required: newData.divisionRequired,
          minSize: newData.minTeamSize || undefined,
          maxSize: newData.maxTeamSize || undefined,
          selectionLocked: newData.teamSelectionLocked,
          switchingDisabled: newData.teamSwitchingDisabled,
          usersCanCreate: newData.userCreatedTeams,
        }
        updateDivisionCategorySettings(payload)
      } else {
        // If no teams and no selected group, still use the new endpoint but with a default category
        // This might need to be adjusted based on your business logic
        updateTeamSettings({
          companyId,
          ...newData,
        })
      }
    } else {
      // Use the existing updateTeamSettings endpoint when teams exist
      updateTeamSettings({
        companyId,
        ...newData,
      })

      // If we have a selected group and the name has changed, also rename the category
      if (
        selectedGroupId &&
        selectedGroup &&
        categoryType === DivisionCategoryType.CUSTOM &&
        customName.trim() !== selectedGroup.customName
      ) {
        renameDivisionCategory({
          groupId: selectedGroupId,
          companyId,
          customName: customName.trim(),
        })
      }
    }
  })

  // Handle delete group
  const handleDeleteGroup = () => {
    if (selectedGroupId) {
      deleteDivisionCategory({
        categoryId: selectedGroupId,
        companyId,
      })
      setDeleteConfirmationOpen(false)
      closeModal()
    }
  }

  // Handle delete button click
  const handleDeleteClick = () => {
    setDeleteConfirmationOpen(true)
  }

  return (
    <>
      <YumButton
        onClick={() => {
          props.clearNotification() // Clear notifications before opening modal
          setModalOpen(true)
        }}
        color="primary"
        variant="contained"
        size="small"
      >
        {t`Settings`}
      </YumButton>

      <YumDialog open={modalOpen}>
        <YumDialogTitle handleClose={handleCancel}>
          {selectedGroupId ? t`Group type settings` : t`Team settings`}
        </YumDialogTitle>
        <YumDialogContent>
          <NotificationsWrapper />

          {/* Category type select and custom name input */}
          {selectedGroupId && selectedGroup && (
            <Box style={{ marginBottom: '16px' }}>
              <DivisionCategoryTypeSelect
                value={categoryType}
                onChange={(newType) => {
                  setCategoryType(newType)
                  // Clear custom name when switching away from CUSTOM type
                  if (newType !== DivisionCategoryType.CUSTOM) {
                    setCustomName('')
                    // Clear any custom name validation errors when switching away from CUSTOM
                    clearErrors('customName')
                  }
                }}
                usedCategoryTypes={usedCategoryTypes}
                style={{ marginBottom: '16px' }}
              />
              <div
                style={{
                  fontSize: '12px',
                  opacity: 0.5,
                  marginTop: '-10px',
                  marginLeft: '10px',
                  marginBottom: categoryType === DivisionCategoryType.CUSTOM ? '16px' : 0,
                }}
              >
                {t`Predefined group types are translated`}
              </div>

              {categoryType === DivisionCategoryType.CUSTOM && (
                <YumField
                  label={t`Custom name`}
                  value={customName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomName(e.target.value)
                    // Clear error when user starts typing
                    if (errors.customName) {
                      clearErrors('customName')
                    }
                  }}
                  style={{ marginBottom: 0 }}
                  inputProps={{ autoComplete: 'off' }}
                  error={!!errors.customName}
                  helperText={errors.customName?.message}
                />
              )}
            </Box>
          )}

          <form onSubmit={onSubmit}>
            <FormGroup>
              <ListItem
                role={undefined}
                dense
                button
                onClick={() => setValue('teamSelectionLocked', !teamSelectionLocked)}
                style={{ marginBottom: 10 }}
              >
                <ListItemIcon>
                  <LockIcon />
                </ListItemIcon>
                <ListItemText
                  primary={selectedGroupId ? t`Only admins can assign groups` : t`Only admins can assign teams`}
                  secondaryTypographyProps={{
                    style: { fontSize: 12 },
                  }}
                />
                <ListItemIcon>
                  <YumFormSwitch control={control} name="teamSelectionLocked" />
                </ListItemIcon>
              </ListItem>
              {!teamSelectionLocked && (
                <>
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => !teamSelectionLocked && setValue('userCreatedTeams', !userCreatedTeams)}
                    style={{ marginBottom: 10 }}
                  >
                    <ListItemIcon>
                      <OrganizerInactiveIcon />
                    </ListItemIcon>
                    <ListItemText primary={selectedGroupId ? t`Users can create groups` : t`Users can create teams`} />
                    <ListItemIcon>
                      <YumFormSwitch control={control} name="userCreatedTeams" />
                    </ListItemIcon>
                  </ListItem>
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => !teamSelectionLocked && setValue('divisionRequired', !divisionRequired)}
                    style={{ marginBottom: 10 }}
                  >
                    <ListItemIcon>
                      <RequireTeamSelectionInactiveIcon />
                    </ListItemIcon>
                    <ListItemText primary={selectedGroupId ? t`Require group selection` : t`Require team selection`} />
                    <ListItemIcon>
                      <YumFormSwitch control={control} name="divisionRequired" />
                    </ListItemIcon>
                  </ListItem>
                  {divisionRequired && !!maxTeamSize ? (
                    <div
                      style={{
                        fontSize: '12px',
                        marginBottom: '16px',
                      }}
                    >
                      {selectedGroupId
                        ? t`If there are no joinable groups, group selection is not required.`
                        : t`If there are no joinable teams, team selection is not required.`}
                    </div>
                  ) : (
                    ''
                  )}
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => !teamSelectionLocked && setValue('teamSwitchingDisabled', !teamSwitchingDisabled)}
                    style={{ marginBottom: 10 }}
                  >
                    <ListItemIcon>
                      <DisableTeamSwitchingInactiveIcon />
                    </ListItemIcon>
                    <ListItemText primary={selectedGroupId ? t`Disable group switching` : t`Disable team switching`} />
                    <ListItemIcon>
                      <YumFormSwitch control={control} name="teamSwitchingDisabled" />
                    </ListItemIcon>
                  </ListItem>
                </>
              )}
              <Controller
                name="minTeamSize"
                rules={{
                  min: 1,
                  max: 1000000,
                  required: false,
                }}
                control={control}
                render={({ field: { ref, ...otherField } }) => (
                  <YumField
                    label={selectedGroupId ? t`Min group size` : t`Min team size`}
                    type="number"
                    inputProps={{
                      inputMode: 'numeric',
                      step: 1, // Prevent browsers from suggesting decimals
                      onKeyDown: (e: any) => {
                        if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+') {
                          e.preventDefault() // Block decimal points
                        }
                      },
                    }}
                    onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
                    error={!!errors.minTeamSize}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          style={{ flexShrink: 0, pointerEvents: 'none' }}
                          position="end"
                          //disableTypography
                        >
                          {t`members`}
                        </InputAdornment>
                      ),
                    }}
                    style={{
                      marginBottom: 10,
                    }}
                    startIcon={GroupInactiveIcon}
                    {...otherField}
                  />
                )}
              />
              <Controller
                name="maxTeamSize"
                rules={{
                  min: 1,
                  max: 1000000,
                  required: false,
                }}
                control={control}
                render={({ field: { ref, ...otherField } }) => (
                  <YumField
                    label={selectedGroupId ? t`Max group size` : t`Max team size`}
                    type="number"
                    inputProps={{
                      inputMode: 'numeric',
                      step: 1, // Prevent browsers from suggesting decimals
                      onKeyDown: (e: any) => {
                        if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+') {
                          e.preventDefault() // Block decimal points
                        }
                      },
                    }}
                    onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
                    error={!!errors.maxTeamSize}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          style={{ flexShrink: 0, pointerEvents: 'none' }}
                          position="end"
                          //disableTypography
                        >
                          {t`members`}
                        </InputAdornment>
                      ),
                    }}
                    style={{
                      marginBottom: 0,
                    }}
                    startIcon={GroupInactiveIcon}
                    /*startIcon={GroupActiveIcon}*/
                    /*startIconInactive={GroupInactiveIcon}*/
                    {...otherField}
                  />
                )}
              />
            </FormGroup>
          </form>

          {/* Delete section for specific groups */}
          {selectedGroupId && (
            <YumButton variant="text" color="error" onClick={handleDeleteClick} disabled={groupsLoading}>
              {t`Delete group type`}
            </YumButton>
          )}
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={handleCancel} submitInProgress={submitInProgress} />
          <YumSubmitButton
            text={t`Update`}
            onClick={() => onSubmit()}
            submitInProgress={submitInProgress}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>

      {/* Delete Confirmation Modal */}
      <YumDialog open={deleteConfirmationOpen}>
        <YumDialogTitle handleClose={() => setDeleteConfirmationOpen(false)}>{t`Delete group type`}</YumDialogTitle>
        <YumDialogContent>
          <Typography variant="body1" style={{ marginBottom: '16px' }}>
            {t`Are you sure you want to delete this group type?`}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
            {t`This action cannot be undone and will permanently remove the group type and all associated data.`}
          </Typography>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={() => setDeleteConfirmationOpen(false)} disabled={groupsLoading} />
          <YumSubmitButton
            text={t`Delete`}
            onClick={handleDeleteGroup}
            disabled={groupsLoading}
            submitInProgress={groupsLoading}
            immersiveLoader={true}
            color="error"
          />
        </YumDialogActions>
      </YumDialog>
    </>
  )
}
